import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

import SFCheckbox from "../checkbox";
import { SFArrowDownIcon, SFKeyboardArrowRightIcon } from "../icons";
import SFTypography from "../typography";
import getStyled from "../../hooks/getStyled";

import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import Collapse from "@mui/material/Collapse";
import _ from "lodash";

import { getGridFilterValues } from "../../services/dataGridService";

const { styled } = getStyled();

const ListItemIconStyled = styled(ListItemIcon)(({ theme }) => ({
  justifyContent: "center",
  minWidth: "40px",
}));

const ListItemButtonStyled = styled(ListItemButton)(({ theme }) => ({
  padding: 0,
}));

const SFTreeFilter = (props) => {
  const [filterState, setFilterState] = useState({
    filterType: "set",
    values: [],
    shouldUpdateGrid: false,
  });
  const [tree, setTree] = useState(null);

  const handleFilterChanged = () => {
    let filterModel = props.api.getFilterModel();
    let model = filterModel[props.colDef.field];
    if (model) {
      setFilterState({ ...model, shouldUpdateGrid: false });
    } else {
      setFilterState({ ...model, shouldUpdateGrid: false });
    }
  };

  useEffect(() => {
    props.api.addEventListener("filterChanged", handleFilterChanged);
    return () => {
      //check if api is destroyed before removing event listeners
      if (props.api?.isDestroyed() === false) {
        props.api.removeEventListener("filterChanged", handleFilterChanged);
      }
    };
  }, []);

  useEffect(() => {
    getGridFilterValues(
      props.colDef.treeFilterValuesUrl,
      JSON.stringify({ colName: props.colDef.field, filterModel: {} })
    )
      .then((resp) => resp.data)
      .then((filterValues) => {
        setTree(filterValues);
      });
  }, []);

  useEffect(() => {
    if (filterState?.shouldUpdateGrid) {
      configureAgGridModel(props.api, filterState, props.colDef.field);
    }
  }, [filterState]);

  return (
    <React.Fragment>
      <div>
        {tree &&
          tree.map((item) => (
            <Branch
              key={item.id}
              item={item}
              setFilter={setFilterState}
              state={filterState}
            />
          ))}
      </div>
    </React.Fragment>
  );
};

export default SFTreeFilter;

const Branch = ({ item, setFilter, state }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [checked, setChecked] = useState(false);
  const hasChildren = item.children && item.children.length !== 0;

  const renderBranches = () => {
    if (hasChildren) {
      return item.children.map((child) => {
        return (
          <Branch
            key={child.id}
            item={child}
            setFilter={setFilter}
            state={state}
          />
        );
      });
    }

    return <Node item={item} setFilter={setFilter} state={state} />;
  };

  useEffect(() => {
    setChecked(state && state?.values?.includes(item.value));
  }, [state]);

  return (
    <List sx={{ pl: 1 }} key={item.id} component="div" disablePadding>
      {hasChildren && (
        <React.Fragment>
          <ListItemButton
            disableGutters
            onClick={() => {
              setOpen((prv) => !prv);
            }}
          >
            <ListItemIconStyled>
              {open && <SFArrowDownIcon />}
              {!open && <SFKeyboardArrowRightIcon></SFKeyboardArrowRightIcon>}
            </ListItemIconStyled>
            <ListItemText>
              <SFTypography variant="body1">
                {item.value} ({item.count})
              </SFTypography>
            </ListItemText>
          </ListItemButton>
          <Collapse in={open} timeout="auto">
            <ListItemButtonStyled
              sx={{ pl: 1 }}
              onClick={() => {
                setChecked((prv) => !prv);
                let resolvedValues = [];
                resolveTreeValues(item, resolvedValues);
                if (!checked) {
                  setFilter((prv) => {
                    return {
                      ...prv,
                      shouldUpdateGrid: true,
                      values: [
                        ...new Set(
                          prv.values
                            ? prv.values.concat(resolvedValues).map((v) => v)
                            : resolvedValues
                        ),
                      ],
                    };
                  });
                } else {
                  setFilter((prv) => {
                    return {
                      ...prv,
                      shouldUpdateGrid: true,
                      values: [
                        ...new Set(
                          prv.values?.filter((x) => !resolvedValues.includes(x))
                        ),
                      ],
                    };
                  });
                }
              }}
            >
              <ListItemIconStyled>
                <SFCheckbox checked={checked}></SFCheckbox>
              </ListItemIconStyled>
              <ListItemText>
                <SFTypography variant="body1">
                  {t("components.datagrid.treeFilter.selectAll")}
                </SFTypography>
              </ListItemText>
            </ListItemButtonStyled>
            {renderBranches()}
          </Collapse>
        </React.Fragment>
      )}
      {!hasChildren && <React.Fragment>{renderBranches()}</React.Fragment>}
    </List>
  );
};

const Node = ({ item, setFilter, state }) => {
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    setChecked(state && state?.values?.includes(item.value));
  }, [state]);
  return (
    <ListItemButtonStyled
      disableGutters
      onClick={() => {
        setChecked((prv) => !prv);
        if (!checked) {
          setFilter((prv) => {
            return {
              ...prv,
              shouldUpdateGrid: true,
              values:
                prv && prv.values
                  ? prv.values.concat([item.value])
                  : [item.value],
            };
          });
        } else {
          setFilter((prv) => {
            return {
              ...prv,
              shouldUpdateGrid: true,
              values: prv.values.filter((x) => x !== item.value),
            };
          });
        }
      }}
    >
      <ListItemIconStyled>
        <SFCheckbox checked={checked}></SFCheckbox>
      </ListItemIconStyled>
      <ListItemText>
        <SFTypography variant="body1">
          {item.value} ({item.count})
        </SFTypography>
      </ListItemText>
    </ListItemButtonStyled>
  );
};

function resolveTreeValues(item, result) {
  result.push(item.value);
  if (item.children && item.children.length > 0) {
    item.children.map((x) => {
      return resolveTreeValues(x, result);
    });
  } else {
    return result;
  }
}

function configureAgGridModel(api, filterState, filterKey) {
  let existingModel = _.cloneDeep(api.getFilterModel()) ?? {};

  if (!filterState || filterState.values.length === 0) {
    delete existingModel[filterKey];
  } else {
    existingModel[filterKey] = {
      filterType: "set",
      values: filterState.values,
    };
  }
  api.setFilterModel(existingModel);
  api.onFilterChanged();
}
